import { logger } from '../utils/logger';

import { COMPLETE_LOCALE_PACKS } from './complete-locale-packs';
import en from './en';
import { ERROR_CODE_I18N, resolveErrorI18nKey } from './error-codes';
import {
    DEFAULT_LOCALE,
    isSupportedLocale,
    resolveDictionaryLocale,
    type DictionaryLocale,
    type Locale,
} from './locales';
import tr from './tr';
import type { TranslationCode, TranslationKey, TranslationNumericKey } from './types';

export type { TranslationCode, TranslationKey, TranslationNumericKey };
export { resolveErrorI18nKey } from './error-codes';

const translations: Partial<Record<Locale, Record<string, string>>> &
    Record<DictionaryLocale, Record<string, string>> = {
    tr: tr as Record<string, string>,
    en: en as Record<string, string>,
    ...COMPLETE_LOCALE_PACKS,
};

/**
 * These legacy dictionary entries contain tenant-specific commercial, legal,
 * provider or service claims. They are intentionally not valid UI fallbacks:
 * such claims must come from the tenant's CMS/API configuration so a newly
 * created site cannot accidentally inherit another operator's promises.
 */
const TENANT_CONFIGURATION_REQUIRED_KEYS: ReadonlySet<string> = new Set([
    '343',
    '344',
    '349', // legacy affiliate/operator marketing promises
    '1651',
    '1653',
    '1655', // unsupported winning, VIP and transaction-speed claims
    '1853', // legacy help centre HTML (providers, payments, licence and timings)
    '1855',
    '1856', // placeholder spin copy and static jackpot promises
    '1862',
    '1867',
    '1868',
    '1870',
    '1871',
    '1872', // affiliate programme and commission promises
    '1884',
    '1886',
    '1887',
    '1888', // placeholder content and unconfigured VIP progression claims
    '1897',
    '1898',
    '1899',
    '1900',
    '1901',
    '1905',
    '1906', // legacy fixed withdrawal and bonus timing claims
    '1984', // legacy VIP benefit/service HTML
    '2055', // operator/licence description
    '2063',
    '2064', // service-rating and sponsorship claims
    '2124',
    '2125',
    '2126',
    '2127',
    '2128',
    '2129',
    '2130',
    '2131', // VIP support, Telegram and automatic bonus claims
    '2133',
    '2134', // legal entity and licence claims
    '2225',
    '2226',
    '2227',
    '2228',
    '2229',
    '2230',
    '2231',
    '2232',
    '2233',
    '2234',
    '2235',
    '2236',
    '2237',
    '2238',
    '2239',
    '2240',
    '2241',
    '2242',
    '2244', // unconfigured VIP rewards, limits and support claims
    '2245',
    '2252', // legacy provider-specific promotion label
    '2253',
    '2280',
    '2281', // old operator slogans
    '2283', // footer licence claim
    '2286', // removed providers.json fallback behaviour
]);

/** Message string → error code (for normalizing backend messages to i18n keys) */
const messageToCode: Record<string, string> = {
    'Bu bonus için kullanılabilir onaylı yatırım bulunamadı.': '2705',
    'Bu bonus için kullanım hakkınız dolmuştur.': '2706',
    'Hoşgeldin bonusu için geçerli yatırım sırasının dışındasınız.': '2707',
    'Bu yatırım için hoşgeldin bonusunu zaten kullandınız.': '2708',
    'Bu yatırım daha önce bir bonus için kullanıldı.': '2709',
    'Bonus talep etmek için kimlik onayı gerekmektedir.': '2710',
    'Bu bonus sadece belirli günlerde geçerlidir.': '2711',
    'Bu bonus sadece belirli saatlerde geçerlidir.': '2712',
    'Bonus talebi işleniyor, lütfen bekleyin.': '2713',
    'Bonus kurallarına uygun değilsiniz.': '2714',
    'Bonus talebi şu anda işlenemiyor.': '2715',
    'Zaten bekleyen bonus talebiniz var.': '2716',
    'Bonus not eligible': '2714',
    // Only these numeric message values are public bonus reason codes.
    '1644': '1644',
    '1677': '1677',
    '160': '160',
    '1648': '1648',
    'Daha önce oluşturulmuş bekleyen bir bonus talebiniz bulunmaktadır.': '1533',
    'Son işleminiz bonus olduğu için yeni bonus talep edemezsiniz.': '1658',
    'Açık kuponunuz olduğu için bonus talep edemezsiniz.': '1659',
    'Son 48 saat içerisinde herhangi bir muhasebe kaydınız olmadığı için bonus talebinde bulunamazsınız.': '1662',
    'Yatırım ve çekimleriniz arasındaki fark bonus için uygun değildir.': '1666',
    'Kayıp bonusu alabilmek için bakiyenizin sıfırlanması gerekmektedir.': '1667',
    'Yatırım bonusu için bakiyenizde para bulunmalıdır.': '1668',
    'Yatırım bonusu talep süresi dolmuştur.': '2719',
    'Son 48 saat içinde çekim işleminiz olduğu için otomatik bonus alamazsınız.': '1684',
    'Hoşgeldin bonusunu daha önce kullandınız.': '2718',
    'Hoşgeldin bonusu sadece ilk yatırımda kullanılabilir.': '2162',
    'Bu bonus için yeterli net kaybınız (Yatırım - Çekim) bulunmamaktadır.': '2717',
    'Net kaybınız bu bonus için belirlenen minimum tutarın altındadır.': '2142',
    'Günlük bonus hakkınızı bugün kullandınız.': '2143',
    'Aynı anda birden fazla bonus talebi oluşturamazsınız.': '1533',
    'Deneme bonusu sadece daha önce bonus kullanmamış kullanıcılar içindir.': '2144',
    'Deneme bonusu sadece sıfır işlemli kullanıcılar içindir.': '2145',
    'Sistem hatası oluştu, lütfen destek ile iletişime geçin.': '2132',
    'İşlem geçmişiniz bulunamadığı için bu kuralı sağlayamıyorsunuz.': '2146',
    'Son 24 saatteki işlemleriniz karda olduğu için bu bonusu alamazsınız.': '2147',
    'Son 48 saatteki işlemleriniz karda olduğu için bu bonusu alamazsınız.': '2148',
    'Aynı IP adresi ile birden fazla hesap tespit edildi.': '2161',
    'Kısa sürede çok fazla işlem yapıldığı için bonus verilemedi.': '2167',
    'Son 7 günde çok sayıda IP adresi kullanıldığı için bonus verilemedi.': '2168',
    'Bonus kullanım limiti aşıldığı için bonus verilemedi.': '2169',
    'Bonus güvenlik kontrolleri nedeniyle verilemedi.': '2170',
    'Bonus kutusu açılmamış': '2153',
    'Invalid Customer ID format': '2157',
    'Customer not found': '1714',
    'Customer account is not active': '2158',
    'Oturum açmanız gerekiyor.': '2159',
    'Geçersiz Site ID': '2160',
    'Zaten aktif bir bonusunuz var.': '2149',
    'Active bonus exists': '160',
    'Bonus bulunamadı veya pasif.': '2150',
    'Bu bonus manuel onay gerektiriyor': '2151',
    'bonus_id sayısal olmalıdır': '2152',
    'Aynı anda sadece 1 bonus talebi açılabilir': '1533',
    'Not eligible for this bonus': '2714',
    'Aktif bonus bulunamadı': '1610',
    'Kullanıcı adı veya şifre hatalı': '2204',
    'Kullanıcı adı veya şifre hatalı.': '2204',
    'Hesap geçici olarak kilitlendi. Lütfen daha sonra tekrar deneyin.': '2499',
    'Doğrulama kodu geçersiz': '2500',
    'Doğrulama kodu geçersiz.': '2500',
    'Doğrulama kodu geçersiz veya süresi dolmuş': '2501',
    'Doğrulama kodu geçersiz veya süresi dolmuş.': '2501',
    'Kurulum oturumu sona erdi. Lütfen yeniden başlatın.': '2502',
    '2FA zaten aktif': '2503',
    '2FA aktif değil': '2504',
    'Şifre hatalı': '303',
    'Bu hesap için 2FA zorunlu. Devre dışı bırakmak için panel yöneticisine başvurun.': '2505',
    'Bu kullanıcı adı zaten kullanılıyor': '2506',
    'Bu e-posta zaten kullanılıyor': '2507',
    'Bu telefon numarası zaten kayıtlı': '2508',
    'Geçerli bir telefon numarası gerekli': '2509',
    'Genel şartlar ve gizlilik bildirimi onayı gerekli': '2510',
    'Hatalı bir TC kimlik numarası girdiniz': '275',
    '18 yaşından küçükler kayıt olamamaktadır': '294',
    'Doğrulama oturumu geçersiz': '2511',
    'Doğrulama oturumu geçersiz veya süresi doldu': '2512',
    'Bu telefon numarasına çok fazla SMS gönderildi. Lütfen daha sonra tekrar deneyin.': '2513',
    'Çark kampanyası aktif değil': '2660',
    'Aktif çark segmenti bulunamadı': '2661',
    'Kullanılabilir çark hakkınız yok': '2662',
    'Geçersiz promosyon kodu': '2663',
    'Bu promosyon kodunu zaten kullandınız': '2664',
    'Wild ödülü bulunamadı veya pasif durumda': '2665',
    'Bu Wild ödülünün stoğu tükenmiş': '2666',
    'Wild bakiyeniz yetersiz': '2667',
    'Bu hesapta sonuçlanmamış bir VIPPay yatırım talebi var. Yeni yatırım için önce mevcut işlemin sonuçlanmasını bekleyin.': '2703',
};

/** Error code → canonical message (O(1) lookup when backend sends code). */
const codeToMessage: Record<string, string> = {};
const defaultTranslationDictionary = translations[DEFAULT_LOCALE] || {};
for (const [code, value] of Object.entries(defaultTranslationDictionary)) {
    if (
        /^\d+$/.test(code) &&
        !TENANT_CONFIGURATION_REQUIRED_KEYS.has(code) &&
        typeof value === 'string' &&
        value.trim()
    ) {
        codeToMessage[code] = value;
    }
}
for (const [msg, code] of Object.entries(messageToCode)) {
    if (!(code in codeToMessage)) {
        codeToMessage[code] = msg;
    }
}

/** Get i18n code for a backend message string. */
export const getCodeForMessage = (message: string): string | null => {
    const code = messageToCode[message.trim()];
    return code && !TENANT_CONFIGURATION_REQUIRED_KEYS.has(code) ? code : null;
};

/** Get canonical message for an error code (when backend sends code directly) */
export const getMessageForCode = (code: string): string | null =>
    TENANT_CONFIGURATION_REQUIRED_KEYS.has(code.trim())
        ? null
        : codeToMessage[code.trim()] ?? null;

const isLocale = (value: string | null | undefined): value is Locale => {
    return isSupportedLocale(value);
};

const readLocalFirst = (key: string): string | null => {
    try {
        const local = localStorage.getItem(key);
        if (local) return local;

        const legacySession = sessionStorage.getItem(key);
        if (!legacySession) return null;

        // Storage SSoT migration: promote legacy session value to local storage.
        localStorage.setItem(key, legacySession);
        sessionStorage.removeItem(key);
        return legacySession;
    } catch {
        return null;
    }
};

const getDictionary = (lang: Locale): Record<string, string> => {
    return translations[lang] || translations[resolveDictionaryLocale(lang)] || {};
};

export const getActiveLocale = (): Locale => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    const pathLang = window.location.pathname.split('/')[1];
    const stored = readLocalFirst('contentLang');
    const candidate = (pathLang || stored || DEFAULT_LOCALE).toLowerCase();
    return isLocale(candidate) ? candidate : DEFAULT_LOCALE;
};

const normalizeCode = (value: unknown): string | null => {
    if (typeof value === 'number') return String(value);
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
        return trimmed;
    }
    return messageToCode[trimmed] ?? null;
};

// Global placeholder values (sitename, domain, year) — set by SiteSettingsContext
let globalValues: Record<string, string | number> = {};

export const setGlobalValues = (values: Record<string, string | number>) => {
    globalValues = { ...globalValues, ...values };
};

const applyValues = (text: string, values?: Record<string, string | number>): string => {
    const merged = { ...globalValues, ...values }; // per-call overrides global
    if (!Object.keys(merged).length) return text;
    return Object.keys(merged).reduce((result, key) => {
        return result.split(`##${key}##`).join(String(merged[key]));
    }, text);
};

export const t = (
    code: TranslationCode,
    options: { lang?: Locale; fallback?: string; values?: Record<string, string | number> } = {}
): string => {
    const lang = options.lang || getActiveLocale();
    const key = String(code);

    if (TENANT_CONFIGURATION_REQUIRED_KEYS.has(key)) {
        if (import.meta.env.DEV) {
            logger.warn(
                'i18n',
                `Tenant-specific claim key "${key}" was suppressed; use CMS/API content instead`,
            );
        }
        return '';
    }

    const dictionary = getDictionary(lang);

    const hasLangValue = Object.prototype.hasOwnProperty.call(dictionary, key);
    const langValue = hasLangValue ? dictionary[key] : undefined;

    const fallbackDictionary = translations[resolveDictionaryLocale(lang)] || translations[DEFAULT_LOCALE];
    const hasFallbackValue = Object.prototype.hasOwnProperty.call(fallbackDictionary, key);
    const fallbackValue = hasFallbackValue ? fallbackDictionary[key] : undefined;

    const raw = langValue ?? options.fallback ?? fallbackValue;
    if (raw === undefined) {
        if (import.meta.env.DEV) {
            logger.warn('i18n', `Missing translation key "${key}" for locale "${lang}"`);
        }
        return '';
    }

    return applyValues(raw, options.values);
};

export const translateApiMessage = (
    message: unknown,
    options: {
        lang?: Locale;
        code?: TranslationCode | null;
        errorCode?: string | null;
        statusCode?: number | null;
        fallback?: string;
        values?: Record<string, string | number>;
    } = {}
): string => {
    const lang = options.lang || getActiveLocale();
    const rawMessage = typeof message === 'string' ? message.trim() : '';
    if (typeof message === 'string') {
        const match = message.match(/^Son işleminiz gerekli türde \((.+)\) değil\.?$/i);
        if (match) {
            return t(2154, { lang, values: { types: match[1] } });
        }
        const userIdMatch = message.match(/^Kullanıcı kimliği doğrulanamadı\. \(ID: (.+)\)$/i);
        if (userIdMatch) {
            return t(2156, { lang, values: { id: userIdMatch[1] } });
        }
        const customerNotFoundMatch = message.match(/^Customer not found: (.+)$/i);
        if (customerNotFoundMatch) {
            return t(2155, { lang, values: { id: customerNotFoundMatch[1] } });
        }
    }

    const messageDerivedCode = normalizeCode(message);
    const fallback = options.fallback || rawMessage;

    if (messageDerivedCode) {
        return t(messageDerivedCode, { lang, fallback, values: options.values });
    }

    const mappedErrorCodeKey =
        options.errorCode && ERROR_CODE_I18N[options.errorCode] !== undefined
            ? ERROR_CODE_I18N[options.errorCode]
            : null;

    if (mappedErrorCodeKey !== null) {
        if (mappedErrorCodeKey === 2207 && rawMessage) {
            return rawMessage;
        }
        return t(mappedErrorCodeKey, {
            lang,
            fallback,
            values: options.values,
        });
    }

    const code = options.code !== undefined && options.code !== null ? String(options.code) : null;

    if (!code) {
        if (options.fallback) {
            return options.fallback;
        }
        if (rawMessage) {
            return rawMessage;
        }
        if (options.statusCode) {
            const statusKey = resolveErrorI18nKey(null, options.statusCode);
            return t(statusKey, { lang, fallback, values: options.values });
        }
        if (options.errorCode) {
            const genericKey = resolveErrorI18nKey(null, null);
            return t(genericKey, { lang, fallback, values: options.values });
        }
        return fallback;
    }

    return t(code, { lang, fallback, values: options.values });
};

/**
 * Strictly typed translation helper — only accepts keys that exist in the dictionary.
 * Use this when you want compile-time validation that the key is valid.
 *
 * @example
 *   tKey(1206)       // ✅ valid numeric key
 *   tKey("1206")     // ✅ valid string key
 *   tKey(9999999)    // ❌ TypeScript error: not a valid key
 */
export const tKey = (
    code: TranslationKey | TranslationNumericKey,
    options: { lang?: Locale; fallback?: string; values?: Record<string, string | number> } = {}
): string => t(code as TranslationCode, options);

export const setTranslations = (lang: Locale, data: Partial<typeof en> | Record<string, string>) => {
    translations[lang] = data;
};

export const getGlobalValues = (): Record<string, string | number> => ({ ...globalValues });
